import { cn } from "@/lib/utils";
import {
    BotMessageSquare,
    Footprints,
    SquareUserRound,
    ThumbsDown,
    ThumbsUp,
    // ThumbsDown,
    // ThumbsUp,
    // ThumbsDown,
    // ThumbsUp,
} from "lucide-react";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

// Import Swiper styles
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import "swiper/css";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import "swiper/css/navigation";

interface ChatBubbleProps {
    children: string;
    orientation?: "left" | "right";
    messageFrom?: "bot" | "user";
    backgroundColor?: string;
    reaction?: number;
    products?: Product[];
    categories?: Category[];
}

type Product = {
    category: string;
    description: string;
    id: string;
    image_url: string;
    name: string;
    price: number;
    product_url: string;
};

type Category = {
    description: string;
    id: string;
    image_url: string;
    name: string;
    url: string;
};

const ChatBubble: React.FC<ChatBubbleProps> = ({
    children,
    orientation = "right",
    messageFrom = "user",
    backgroundColor,
    reaction,
    products,
    categories,
}) => {
    return (
        <>
            <div className={cn(orientation === "left" ? "me-11" : "ms-auto  flex-row-reverse", "flex  max-w-lg  gap-x-2 sm:gap-x-4 ")}>
                {messageFrom === "bot" ? (
                    <div className="flex size-8 items-center justify-center rounded-full bg-gray-200 p-1">
                        <BotMessageSquare className="size-5 text-gray-600" />
                    </div>
                ) : (
                    <div className="flex size-8 items-center justify-center rounded-full bg-gray-200 p-1">
                        <SquareUserRound className="size-5 text-gray-600" />
                    </div>
                )}

                <div>
                    <div
                        className={cn(
                            orientation === "right" ? "rounded-xl rounded-br-none  text-white" : "rounded-xl rounded-bl-none  text-gray-800",
                            "space-y-3 border border-gray-200  p-3"
                        )}
                        style={{
                            background: orientation === "right" ? backgroundColor || "#96cef3" : "white",
                        }}
                    >
                        <div className="space-y-1.5">
                            <p className="mb-1.5 text-sm ">{children}</p>
                        </div>
                    </div>

                    {messageFrom === "bot" && (
                        <span
                            className={cn(
                                orientation === "right" ? "justify-self-start" : "justify-self-end",
                                "mt-1.5 flex items-center  text-xs text-gray-500"
                            )}
                        >
                            <button className="rounded-l-md border border-r-0 p-1">
                                <ThumbsUp className={cn("size-4", reaction === 1 && "fill-blue-500")} />
                            </button>
                            <button className="rounded-r-md border p-1">
                                <ThumbsDown className={cn("size-4", reaction === 2 && "fill-blue-500")} />
                            </button>
                        </span>
                    )}
                </div>
            </div>
            {products && products?.length === 1 && (
                <div className="mt-2">
                    <Swiper
                        spaceBetween={60}
                        slidesPerView={2}
                        centeredSlides={true}
                        navigation={true}
                        modules={[Navigation]}
                    >
                        {products.map((product) => (
                            <SwiperSlide key={product.id} zoom={true}>
                                <a
                                    href={product?.product_url}
                                    className="max-w-[150px] min-w-[150px] h-[220px] border border-gray-200 rounded-xl p-3 text-decoration-none flex flex-col justify-between items-start bg-white shadow-md transition-transform duration-200 hover:shadow-lg hover:-translate-y-0.5"
                                >
                                    <div className="w-full h-[130px] flex items-center justify-center mb-2 overflow-hidden rounded-lg bg-gray-50">
                                        {product?.image_url ? (
                                            <img src={product?.image_url} alt={product?.name || "Product"} className="w-full h-full object-cover rounded-lg" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <Footprints className="h-8 w-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex-grow flex flex-col justify-end gap-1">
                                        <h4 className="text-sm font-bold text-gray-800 text-left leading-tight line-clamp-2 h-[50px] overflow-hidden break-words">
                                            {product?.name}
                                        </h4>
                                        {product?.price && (
                                            <p className="text-sm font-bold text-blue-500 text-left">
                                                ${product?.price}
                                            </p>
                                        )}
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}
            {products && products?.length > 1 && (
                <div className="mt-2">
                    <Swiper
                        className="w-[250px]"
                        spaceBetween={60}
                        slidesPerView={2}
                        centeredSlides={true}
                        navigation={true}
                        modules={[Navigation]}
                    >
                        {products.map((product) => (
                            <SwiperSlide key={product.id} zoom={true}>
                                <a
                                    href={product?.product_url}
                                    className="max-w-[150px] min-w-[150px] h-[220px] border border-gray-200 rounded-xl p-3 text-decoration-none flex flex-col justify-between items-start bg-white shadow-md transition-transform duration-200 hover:shadow-lg hover:-translate-y-0.5"
                                >
                                    <div className="w-full h-[130px] flex items-center justify-center mb-2 overflow-hidden rounded-lg bg-gray-50">
                                        {product?.image_url ? (
                                            <img src={product?.image_url} alt={product?.name || "Product"} className="w-full h-full object-cover rounded-lg" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <Footprints className="h-8 w-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex-grow flex flex-col justify-end gap-1">
                                        <h4 className="text-sm font-bold text-gray-800 text-left leading-tight line-clamp-2 h-[50px] overflow-hidden break-words">
                                            {product?.name}
                                        </h4>
                                        {product?.price && (
                                            <p className="text-sm font-bold text-blue-500 text-left">
                                                ${product?.price}
                                            </p>
                                        )}
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}

            {categories && categories?.length === 1 && (
                <div className="mt-2">
                    <Swiper
                        spaceBetween={60}
                        slidesPerView={2}
                        centeredSlides={true}
                        navigation={true}
                        modules={[Navigation]}
                    >
                        {categories.map((category) => (
                            <SwiperSlide key={category.id} zoom={true}>
                                <a
                                    href={category?.url}
                                    className="max-w-[150px] min-w-[150px] h-[220px] border border-gray-200 rounded-xl p-3 text-decoration-none flex flex-col justify-between items-start bg-white shadow-md transition-transform duration-200 hover:shadow-lg hover:-translate-y-0.5"
                                >
                                    <div className="w-full h-[130px] flex items-center justify-center mb-2 overflow-hidden rounded-lg bg-gray-50">
                                        {category?.image_url ? (
                                            <img src={category?.image_url} alt={category?.name || "Category"} className="w-full h-full object-cover rounded-lg" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <Footprints className="h-8 w-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex-grow flex flex-col justify-end gap-1">
                                        <h4 className="text-sm font-bold text-gray-800 text-left leading-tight line-clamp-2 h-[50px] overflow-hidden break-words">
                                            {category?.name}
                                        </h4>
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}
            {categories && categories?.length > 1 && (
                <div className="mt-2">
                    <Swiper
                        className="w-[250px]"
                        spaceBetween={60}
                        slidesPerView={2}
                        centeredSlides={true}
                        navigation={true}
                        modules={[Navigation]}
                    >
                        {categories.map((category) => (
                            <SwiperSlide key={category.id} zoom={true}>
                                <a
                                    href={category?.url}
                                    className="max-w-[150px] min-w-[150px] h-[220px] border border-gray-200 rounded-xl p-3 text-decoration-none flex flex-col justify-between items-start bg-white shadow-md transition-transform duration-200 hover:shadow-lg hover:-translate-y-0.5"
                                >
                                    <div className="w-full h-[130px] flex items-center justify-center mb-2 overflow-hidden rounded-lg bg-gray-50">
                                        {category?.image_url ? (
                                            <img src={category?.image_url} alt={category?.name || "Category"} className="w-full h-full object-cover rounded-lg" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <Footprints className="h-8 w-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex-grow flex flex-col justify-end gap-1">
                                        <h4 className="text-sm font-bold text-gray-800 text-left leading-tight line-clamp-2 h-[50px] overflow-hidden break-words">
                                            {category?.name}
                                        </h4>
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}
        </>
    );
};

export default ChatBubble;
